/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { MutableRef } from 'preact/hooks';
import { useUser } from '../UNSAFE_useUser';
import { handleInput, cancelEvent } from './events';
import { CommitDetail, InputDetail } from '../../utils/UNSAFE_meterTypes';

export function useKeyboardEvents(
  value: number,
  min: number,
  max: number,
  step: number,
  currentInputRef: MutableRef<number | undefined>,
  onCommit?: (detail: CommitDetail) => void,
  onInput?: (detail: InputDetail) => void
) {
  const { direction } = useUser();

  const isRtl = direction === 'rtl';

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;

    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown':
        handleInput(Math.max(min, value - step), currentInputRef, onInput);
        break;
      case 'ArrowUp':
        handleInput(Math.min(max, value + step), currentInputRef, onInput);
        break;
      case 'ArrowLeft': {
        const inputValue = isRtl ? Math.min(max, value + step) : Math.max(min, value - step);
        handleInput(inputValue, currentInputRef, onInput);
        break;
      }
      case 'ArrowRight': {
        const inputValue = isRtl ? Math.max(min, value - step) : Math.min(max, value + step);
        handleInput(inputValue, currentInputRef, onInput);
        break;
      }
    }
    cancelEvent(event);
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.key;

    switch (key) {
      case 'Enter':
        onCommit?.({ value: value } as CommitDetail);
        break;
      case 'Tab':
        handleInput(value, currentInputRef, onInput);
        break;
      case 'Home':
        handleInput(min, currentInputRef, onInput);
        break;
      case 'End':
        handleInput(max, currentInputRef, onInput);
        break;
    }
    cancelEvent(event);
  };

  return {
    onKeyUp: keyUpHandler,
    onKeyDown: keyDownHandler
  };
}
